/**
 * Capability × persona (≥ lg): "is this weakness universal or one
 * persona's?". A real table with row and column headers; every cell
 * shows its number, the tint only repeats it. Not walked reads as text.
 */
import type {
  HotspotFilters,
  HotspotMatrixCell,
  HotspotWindow,
} from "@iamu/shared";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/utils";
import { formatPressure } from "./hotspot-vocabulary";

/** Single-hue steps; dark text stays ≥ 7:1 on every tint in both themes. */
const TINTS = [
  "bg-mark-critical/5",
  "bg-mark-critical/10",
  "bg-mark-critical/15",
  "bg-mark-critical/25",
];

function tint(cell: HotspotMatrixCell, max: number): string {
  if (cell.status !== "measured" || cell.pressure == null || max <= 0)
    return "";
  const i = Math.min(
    TINTS.length - 1,
    Math.floor((cell.pressure / max) * TINTS.length),
  );
  return TINTS[i];
}

export function HotspotMatrix({
  projectId,
  window,
  filters,
  includeSecondary,
  hrefFor,
}: {
  projectId: string;
  window: HotspotWindow;
  filters: HotspotFilters;
  includeSecondary: boolean;
  hrefFor: (key: string) => string;
}) {
  const { data, isLoading } = trpc.hotspot.matrix.useQuery({
    projectId,
    window,
    filters,
    includeSecondary,
  });
  if (isLoading || !data) return <Skeleton className="h-48 w-full" />;
  const max = Math.max(
    0,
    ...Object.values(data.cells).flatMap((row) =>
      Object.values(row).map((c) =>
        c.status === "measured" ? (c.pressure ?? 0) : 0,
      ),
    ),
  );
  return (
    <Table aria-label="Pressure by capability and persona">
      <TableCaption className="text-left">
        Pressure per capability for each persona, smoothed toward the same
        project prior. Darker cells are weaker; "clear" means walked with no
        unresolved problems, "not walked" means that persona has no run there in
        this window.
      </TableCaption>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead scope="col" className="px-3">
            Capability
          </TableHead>
          {data.cols.map((c) => (
            <TableHead key={c.key} scope="col" className="px-3 text-right">
              {c.label}
            </TableHead>
          ))}
        </TableRow>
      </TableHeader>
      <TableBody>
        {data.rows.map((r) => (
          <TableRow key={r.key} className="hover:bg-transparent">
            <TableHead scope="row" className="px-3 font-semibold">
              <Link
                to={hrefFor(r.key)}
                className="underline-offset-4 hover:underline"
              >
                {r.label}
              </Link>
            </TableHead>
            {data.cols.map((c) => {
              const cell = data.cells[r.key]?.[c.key];
              const walked = cell && cell.status !== "not_walked";
              return (
                <TableCell
                  key={c.key}
                  className={cn(
                    "px-3 text-right tabular-nums",
                    cell && tint(cell, max),
                  )}
                  style={
                    walked
                      ? undefined
                      : {
                          backgroundImage:
                            "repeating-linear-gradient(135deg, var(--muted) 0 3px, transparent 3px 7px)",
                        }
                  }
                >
                  {walked ? (
                    <>
                      <span className="text-base font-semibold">
                        {formatPressure(cell.pressure)}
                      </span>
                      {cell.status === "clear" && (
                        <span className="block text-xs text-muted-foreground">
                          clear
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="text-sm text-foreground">not walked</span>
                  )}
                </TableCell>
              );
            })}
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}
